/**
 * Audio en: https://whaencinbi.club/audios/{sucursal}/{uniqueid}.wav
 * Ej. https://whaencinbi.club/audios/Sucursal%20Centro/1775228826.94.wav
 *
 * - `recordfiles`: indica si existe grabación (flag / valor truthy), no es la carpeta ni sustituye al uniqueid en la URL.
 * - `uniqueid`: nombre del fichero (se añade .wav si falta).
 * - `sucursal`: nombre de carpeta tal cual en disco (ej. "Sucursal Centro", con espacios).
 */
import { getRecording } from '@/api/telefonia'

export const TELEFONIA_AUDIOS_PUBLIC_BASE = 'https://whaencinbi.club/audios'

/**
 * ¿Hay audio disponible según el campo recordfiles del CDR?
 * Acepta 1, true, ruta no vacía, etc.; excluye 0, false, "", "no".
 */
export function recordfilesIndicaAudio (recordfiles) {
  if (recordfiles == null) return false
  if (typeof recordfiles === 'boolean') return recordfiles
  if (typeof recordfiles === 'number') return recordfiles !== 0
  const s = String(recordfiles).trim()
  if (s === '' || s === '0' || s.toLowerCase() === 'false' || s.toLowerCase() === 'no') return false
  return true
}

/**
 * Nombre del fichero en la URL: solo desde uniqueid → `{uniqueid}.wav` (sin transformar auto-…)
 */
export function filenameFromUniqueid (uniqueid) {
  if (uniqueid == null || String(uniqueid).trim() === '') return ''
  let u = String(uniqueid).trim()
  while (u.endsWith('@')) u = u.slice(0, -1).trim()
  if (!u) return ''
  if (/\.wav$/i.test(u)) return u
  return `${u}.wav`
}

/**
 * Nombre del .wav en /audios/{sucursal}/ como en disco.
 * El CDR a veces trae `auto-1775087690-1001-8113120192` pero el fichero real es el uniqueid Asterisk `1775087690.1001.wav` (dos números separados por punto).
 */
export function canonicalWavFilename (uniqueid) {
  if (uniqueid == null || String(uniqueid).trim() === '') return ''
  let s = String(uniqueid).trim()
  while (s.endsWith('@')) s = s.slice(0, -1).trim()
  if (!s) return ''
  if (/^\d+\.\d+\.wav$/i.test(s)) return s
  if (/^\d+\.\d+$/i.test(s)) return `${s}.wav`
  const m = s.match(/^auto-(\d+)-(\d+)-(\d+)(?:\.wav)?$/i)
  if (m) {
    return `${m[1]}.${m[2]}.wav`
  }
  return filenameFromUniqueid(s)
}

/** @deprecated usar filenameFromUniqueid; se mantiene por compatibilidad */
export function sanitizeRecordingFilename (recordfiles) {
  if (recordfiles == null) return ''
  let s = String(recordfiles).trim()
  while (s.endsWith('@')) {
    s = s.slice(0, -1).trim()
  }
  const i = s.lastIndexOf('/')
  return i >= 0 ? s.slice(i + 1) : s
}

export function inferSucursalFromBasename (filename) {
  if (!filename) return null
  const fn = String(filename).trim()
  const mAuto = fn.match(/^auto-\d+-(\d+)-/i)
  if (mAuto) return mAuto[1]
  return null
}

/** Solo si recordfiles trae ruta legacy `carpeta/archivo.wav` */
export function extractSucursalFromRecordfilesPath (recordfiles) {
  let raw = String(recordfiles || '').trim()
  while (raw.endsWith('@')) {
    raw = raw.slice(0, -1).trim()
  }
  if (!raw || raw.indexOf('/') === -1) return null
  const segments = raw.split('/').filter(Boolean)
  if (segments.length < 2) return null
  return String(segments[segments.length - 2]).trim() || null
}

/**
 * URL pública: /audios/{sucursal}/{uniqueid}.wav
 * `sucursalOverride` = nombre carpeta (ej. "Sucursal Centro").
 */
export function buildPublicRecordingUrl (recordfiles, sucursalOverride, uniqueid) {
  const fn = canonicalWavFilename(uniqueid)
  if (!fn) return null
  let sucursal =
    sucursalOverride != null && String(sucursalOverride).trim() !== ''
      ? String(sucursalOverride).trim()
      : null
  if (!sucursal) {
    sucursal = extractSucursalFromRecordfilesPath(recordfiles)
  }
  if (!sucursal) return null
  const base = TELEFONIA_AUDIOS_PUBLIC_BASE.replace(/\/$/, '')
  return `${base}/${encodeURIComponent(sucursal)}/${encodeURIComponent(fn)}`
}

async function parseErrorBlob (blob) {
  try {
    const text = await blob.text()
    try {
      const j = JSON.parse(text)
      return (j && (j.message || j.error)) || text || 'Error del servidor'
    } catch {
      return text || 'Error del servidor'
    }
  } catch {
    return 'Error del servidor'
  }
}

export async function fetchRecordingBlob (recordfiles, sucursalOverride, uniqueid) {
  const filename = canonicalWavFilename(uniqueid)
  if (!filename) {
    throw new Error('Falta uniqueid para armar el nombre del audio')
  }

  const publicUrl = buildPublicRecordingUrl(recordfiles, sucursalOverride, uniqueid)
  if (publicUrl) {
    try {
      const res = await fetch(publicUrl, { mode: 'cors', credentials: 'omit' })
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }
      const blob = await res.blob()
      const ct = (res.headers && res.headers.get('content-type')) || ''
      if (blob && typeof blob.text === 'function' && ct.indexOf('json') !== -1) {
        const msg = await parseErrorBlob(blob)
        throw new Error(msg)
      }
      return blob
    } catch (e1) {
      try {
        const res = await getRecording({ filename })
        const blob = res.data
        const ct = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || ''
        if (blob && typeof blob.text === 'function' && ct.indexOf('json') !== -1) {
          const msg = await parseErrorBlob(blob)
          throw new Error(msg)
        }
        return blob
      } catch (e2) {
        if (e2.response && e2.response.data instanceof Blob) {
          const msg = await parseErrorBlob(e2.response.data)
          throw new Error(msg)
        }
        const hint = e1 && e1.message ? e1.message : 'red'
        throw new Error(`Audio no disponible (${hint}).`)
      }
    }
  }

  try {
    const res = await getRecording({ filename })
    const blob = res.data
    const ct = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || ''
    if (blob && typeof blob.text === 'function' && ct.indexOf('json') !== -1) {
      const msg = await parseErrorBlob(blob)
      throw new Error(msg)
    }
    return blob
  } catch (e) {
    if (e.response && e.response.data instanceof Blob) {
      const msg = await parseErrorBlob(e.response.data)
      throw new Error(msg)
    }
    throw new Error('Falta nombre de sucursal (carpeta) para la URL de audio o el audio no está en el servidor.')
  }
}

export async function getRecordingObjectUrl (recordfiles, sucursalOverride, uniqueid) {
  const direct = buildPublicRecordingUrl(recordfiles, sucursalOverride, uniqueid)
  if (direct) return direct
  const blob = await fetchRecordingBlob(recordfiles, sucursalOverride, uniqueid)
  return URL.createObjectURL(blob)
}
